import React, { useEffect, useState, useCallback } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { Row, Col, Button } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import supplierApis from "../../../api/master/supplier";
import Swal from "sweetalert2";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import materialsApis from "../../../api/master/materials";
import usersApis from "../../../api/master/users";
import GstinVerificationModal from "../../../components/GstinVerificationModal";
import FormDatePicker from "../../form-components/DatePicker";
import dayjs from "dayjs";
import useSequence from "../../../hooks/useSequence";
interface SelectOptions {
  label: string;
  value: string;
}
const GeneralInformation: React.FC = (props: any) => {
  const methods = useForm();
  const { hasSequence } = useSequence(159);
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    getValues,
    clearErrors,
  } = useFormContext();
  const FullFormDetails = watch();
  const materials = watch("materials");
  const businessType = watch("bussinessType");

  useEffect(() => {
    if (materials?.length > 0) {
      findCategoryByMaterialTypeId(materials);
    }
  }, [materials]);

  const [categories, setCategories] = useState<SelectOptions[]>([]);
  const [industryTypeOptions, setIndustryTypeOptions] = useState<
    SelectOptions[]
  >([]);
  const [disableCategory, setDisableCategory] = useState(true);

  const [gstinModal, setGstinModal] = useState(false);
  const [states, setStates] = useState<SelectOptions[]>([]);
  const [districtOptions, setDistrictOptions] = useState<SelectOptions[]>([]);
  const [countries, setCountries] = useState<SelectOptions[]>([]);
  const [previousBusinessType, setPreviousBusinessType] = useState<string>("");
  const [isSettingGstData, setIsSettingGstData] = useState(false);

  const getCountries = async () => {
    try {
      const _countries = await usersApis.getCountrys();
      if (_countries.status) {
        if (_countries.data.length > 0) {
          const transformedData = _countries.data.map((item: any) => ({
            value: item.countryId.toString(),
            label: item.countryName,
          }));
          setCountries(transformedData);
        }
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const fetchStatesByCountry = async (countryId: string) => {
    try {
      const data = await usersApis.getStatesByCountry(countryId);
      const stateOptions = data?.data?.map((state: any) => ({
        value: state.stateId.toString(),
        label: state.stateName,
      }));
      if (stateOptions.length > 0) setStates(stateOptions);
    } catch (error) { }
  };
  const getDistricts = async (stateID: any) => {
    try {
      const districts = await usersApis.getDistrictsByState(stateID);
      console.log(districts);
      if (districts.status) {
        if (districts.data.length > 0) {
          const transformedData = districts.data.map((item: any) => ({
            value: item.districtId.toString(),
            label: item.districtName,
          }));
          setDistrictOptions(transformedData);
        }
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const fetchIndustryType = async () => {
    try {
      const { data, status, message } =
        await supplierApis.fetchMaterialTyepesByCategory();
      if (status) {
        setIndustryTypeOptions(
          data.map((obj: any) => ({ label: obj.name, value: obj.material_id }))
        );
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
  };

  const countryWatch = watch("country");
  const stateWatch = watch("state");
  useEffect(() => {
    if (countryWatch) {
      fetchStatesByCountry(countryWatch);
    }
  }, [countryWatch]);
  useEffect(() => {
    if (stateWatch) {
      getDistricts(stateWatch);
    }
  }, [stateWatch]);

  useEffect(() => {
    fetchIndustryType();
    getCountries();
    fetchStatesByCountry("1");
  }, []);

  const findCategoryByMaterialTypeId = async (materialTypeId: any) => {
    const idsString = materialTypeId && materialTypeId.join(",");
    try {
      const { data, status, message } =
        await materialsApis.findCategoryByMaterialType(idsString);
      if (status) {
        setCategories(
          data.map((obj: any) => ({ label: obj.category, value: obj.id }))
        );
        setDisableCategory(false);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
  };

  useEffect(() => {
    if (previousBusinessType && previousBusinessType !== businessType) {
      // Only reset GST-related fields, not all fields
      resetGstRelatedFields();
    }
    setPreviousBusinessType(businessType);
  }, [businessType]);

  const resetGstRelatedFields = () => {
    clearErrors();
    // Only reset GST-specific fields
    const gstFieldsToReset = [
      "gstin",
      "cin",
      "incorporationDate",
      "pan",
      "companyName",
      "country",
      "state",
      "district",
      "city",
      "area",
      "mailingAddressLine1",
      "pincode",
      "lastFYTurnover",
      "applicationAccess",
    ];

    gstFieldsToReset.forEach((field) => {
      setValue(field, "", { shouldValidate: false });
    });

    // setTimeout(() => {
    //   trigger();
    // }, 100);
  };

  const isGstVerified = () => {
    const values = getValues();
    return !!values.gstin && !!values.pan;
  };

  useEffect(() => {
    if (businessType === "registered" && !isGstVerified()) {
      setGstinModal(true);
    } else {
      setGstinModal(false);
    }
  }, [businessType]);

  const findOptionValue = (label: string, options: SelectOptions[]) => {
    const found = options.find((option) => option.label === label);
    return found ? found.value : "";
  };

  const handleGSTVerification = async (gstData: any) => {
    console.log("Verified GST Data:", gstData);

    setIsSettingGstData(true);
    clearErrors();

    // Improved district matching logic
    let districtValue = "";
    if (gstData.district) {
      const districtByName = districtOptions.find(
        (option) =>
          option.label.toLowerCase() === gstData.district.toLowerCase()
      );
      const districtByValue = districtOptions.find(
        (option) =>
          option.value.toLowerCase() === gstData.district.toLowerCase()
      );
      districtValue = districtByName?.value || districtByValue?.value || "";
    }

    // Format the incorporation date if it exists
    let formattedIncorporationDate: any = null;
    if (gstData.incorporationDate) {
      const parsed = dayjs(gstData.incorporationDate);
      if (parsed.isValid()) {
        formattedIncorporationDate = parsed;
      }
    }

    setValue("incorporationDate", formattedIncorporationDate, {
      shouldValidate: false,
    });

    // Set all values first without validation
    const updates = [
      () => setValue("gstin", gstData.gstin || "", { shouldValidate: false }),
      () => setValue("pan", gstData.pan || "", { shouldValidate: false }),
      () =>
        setValue("supplierName", gstData.LegalName || "", {
          shouldValidate: false,
        }),
      () =>
        setValue("tradeName", gstData.TradeName || gstData.LegalName || "", {
          shouldValidate: false,
        }),
      () =>
        setValue("country", findOptionValue(gstData.country, countries) || "", {
          shouldValidate: false,
        }),
      () =>
        setValue("state", findOptionValue(gstData.state, states) || "", {
          shouldValidate: false,
        }),
      // () =>
      //   setValue(
      //     "district",
      //     findOptionValue(gstData.district, districtOptions) || "",
      //     { shouldValidate: false }
      //   ),
      () => setValue("district", districtValue, { shouldValidate: false }),

      () => setValue("city", gstData.city || "", { shouldValidate: false }),
      () => setValue("area", gstData.area || "", { shouldValidate: false }),
      () =>
        setValue("mailingAddressLine1", gstData.addressLine1 || "", {
          shouldValidate: false,
        }),
      () =>
        setValue("pincode", gstData.zipPincode || "", {
          shouldValidate: false,
        }),
    ];

    // Execute all setValue calls
    updates.forEach((update) => update());

    // Wait for React to process the state updates
    setTimeout(() => {
      // Trigger validation for all relevant fields
      const fieldsToValidate = [
        "gstin",
        "pan",
        "companyName",
        "tradeName",
        "country",
        "state",
        "district",
        "city",
        "area",
        "mailingAddressLine1",
        "pincode",
      ];

      trigger(fieldsToValidate).then((isValid) => {
        console.log("Validation result after GST data:", isValid);
        setIsSettingGstData(false);
      });
    }, 300);
  };

  return (
    <>
      {/* <FormProvider {...methods}> */}
      <form
        className="content clearfix"
        onSubmit={handleSubmit(() => {
          props.next();
        })}
      >
        <h5 className="mb-2">Legal Information</h5>
        <Row className="gy-4">
          <Col lg="4">
            <FormSelect
              name="bussinessType"
              label="Business Type"
              required={true}
              options={[
                { label: "Registered", value: "registered" },
                { label: "Unregistered", value: "unregistered" },
              ]}
              placeholder="Select Business Type"
            />
          </Col>
          {/* Conditional Fields */}
          {businessType === "registered" && (
            <>
              <Col lg="4">
                <FormInput
                  name="gstin"
                  label="GSTIN"
                  required
                  type="text"
                  placeholder="Auto-filled GSTIN"
                  disabled
                />
              </Col>

              <Col lg="4">
                <FormInput
                  name="cin"
                  label="CIN"
                  type="text"
                // placeholder="Auto-filled CIN"
                // disabled
                />
              </Col>

              <Col lg="4">
                <FormDatePicker
                  name="incorporationDate"
                  label="Incorporation Date"
                  required
                />
              </Col>

              <Col lg="4">
                <FormInput
                  name="lastFYTurnover"
                  label="Last FY Turn Over (INR)"
                  type="text"
                  placeholder="Enter Last FY Turnover"
                  pattern={/^\d+$/}
                />
              </Col>
            </>
          )}
          {(businessType === "registered" ||
            businessType === "unregistered") && (
              <Col lg="4">
                <FormInput
                  name="pan"
                  label="PAN"
                  required
                  type="text"
                  pattern={/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/}
                  placeholder={
                    businessType === "registered"
                      ? "Auto-filled PAN"
                      : "Enter PAN"
                  }
                  disabled={businessType === "registered"}
                />
              </Col>
            )}

          {businessType === "unregistered" && (
            <Col lg="4">
              <FormInput
                name="cin"
                label="CIN"
                type="text"
                placeholder="Enter CIN"
              />
            </Col>
          )}
        </Row>

        <h5 className="mt-4 mb-2">General Information</h5>
        <Row className="gy-4">
          {!hasSequence && (
            <Col lg="4">
              <FormInput
                name="referenceID"
                label="Supplier ID"
                type="text"
                pattern={/^[a-zA-Z0-9./_-]+$/}
                required={true}
                // maxLength={10}
                placeholder="Enter Supplier ID"
              />
            </Col>
          )}
          <Col lg="4">
            <FormInput
              name="supplierName"
              label="Supplier Name(Legal Name)"
              // disabled={FullFormDetails?.gstinVerified == "true"}
              // disabled={action === "view"}
              disabled={businessType === "registered"}
              required={true}
              pattern={/^[A-Za-z\s!@#$%^&*(),.?":{}|<>/&.-]+$/}
              type="text"
              // maxLength={50}
              placeholder="Enter Supplier Name"
              onInput={(e: any) => {
                e.target.value = e.target.value.replace(/[^A-Za-z\s.&/-]/g, ""); // Removes invalid characters
              }}
            />
          </Col>
          <Col lg="4">
            <FormMultiSelect
              name="materials"
              label="Material Type"
              options={industryTypeOptions}
              required={true}
              placeholder="Select Material Type"
              onBlur={(event, values) => {
                setValue("categories", []);
              }}
            />
            {/* <label className="form-label">
              Material Type<span className="mandatory">*</span>
            </label>
            <RSelect
              isMulti
              name="industryType"
              // disabled={action === "view"}
              label="Material Type"
              options={industryTypeOptions}
              required={true}
              placeholder="-Select-"
              onChange={(value: any) =>
                handleMultiSelectChange(value, "materials")
              }
              closeMenuOnSelect={false}
            /> */}
          </Col>
        </Row>
        <Row className="gy-4 mt-1">
          <Col lg="4">
            <FormMultiSelect
              name="categories"
              label="Supply Categories"
              required={true}
              options={categories}
              disabled={disableCategory}
              placeholder="Select Supply Categories"
            />
          </Col>
          <Col lg="4">
            <FormInput
              name="tradeName"
              label="Trade Name"
              required={true}
              type="text"
              placeholder="Enter Trade Name"
              pattern={/^[A-Za-z\s]+$/}
              onInput={(e: any) => {
                e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, ""); // Removes invalid characters
              }}
              disabled={businessType === "registered"}
            />
          </Col>
          <Col lg="4">
            <FormInput
              name="brandName"
              label="Brand Name"
              required={true}
              type="text"
              placeholder="Enter Brand Name"
            />
          </Col>
          {/* <Col lg="4">
            <FormInput
              name="minSupply"
              label="Min.Supply"
              // required={true}
              type="number"
              // disabled={action === "view"}
              placeholder="Enter Min.Supply"
            />
          </Col> */}
          <Col lg="4">
            <FormInput
              name="deliveryLeadDays"
              label="Delivery Lead Days"
              required={true}
              pattern={/^\d+$/}
              type="number"
              // disabled={action === "view"}
              placeholder="Enter Delivery Lead Days"
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="paymentTerms"
              label="Payment Terms"
              required={true}
              options={[
                { label: "Advance", value: "Advance" },
                { label: "Credit", value: "Credit" },
              ]}
              placeholder="Enter Payment Terms"
            />
          </Col>
          {watch("paymentTerms") == "Credit" && (
            <Col lg="4">
              <FormInput
                name="creditDays"
                label="Credit Days"
                required={true}
                type="number"
                placeholder="Enter Credit Days"
              />
            </Col>
          )}

          {/* <Col lg="4">
            <FormMultiSelect
              name="supplyStates"
              label="Supply State"
              options={states}
              placeholder="Select Supply State"
            />
          </Col> */}
        </Row>
        <Row className="gy-4 mt-1">
          <Col>
            <Button color="primary" type="submit">
              Next
            </Button>
          </Col>
        </Row>
      </form>
      {/* </FormProvider> */}

      {/* GstinVerificationModal usage: */}
      <GstinVerificationModal
        isOpen={gstinModal}
        toggle={() => setGstinModal(!gstinModal)}
        // onVerified={async (gstData: any) => {
        //   console.log("Verified GST Data:", gstData);

        //   // Set values with validation
        //   setValue("gstin", gstData.gstin, { shouldValidate: true });
        //   setValue("pan", gstData.pan, { shouldValidate: true });
        //   setValue("companyName", gstData.LegalName, {
        //     shouldValidate: true,
        //   });

        //   console.log("Districts:", districtOptions);

        //   console.log("GST District:", gstData.district);

        //   // setValue("country", gstData.country, { shouldValidate: true });
        //   // setValue("state", gstData.state, { shouldValidate: true });
        //   // setValue("district", gstData.district, { shouldValidate: true });

        //   setValue("country", findOptionValue(gstData.country, countries), {
        //     shouldValidate: true,
        //   });
        //   setValue("state", findOptionValue(gstData.state, states), {
        //     shouldValidate: true,
        //   });
        //   setValue(
        //     "district",
        //     findOptionValue(gstData.district, districtOptions),
        //     { shouldValidate: true }
        //   );

        //   setValue("city", gstData.city, { shouldValidate: true });
        //   setValue("area", gstData.area, { shouldValidate: true });
        //   setValue("mailingAddressLine1", gstData.addressLine1, {
        //     shouldValidate: true,
        //   });
        //   setValue("pincode", gstData.zipPincode, { shouldValidate: true });

        //   // Optional: Trigger full form validation
        //   setTimeout(() => {
        //     trigger();
        //   }, 100);
        //   trigger();
        // }}
        onVerified={handleGSTVerification}
        countries={countries}
        states={states}
        districts={districtOptions}
        source="suppliers"
      />
    </>
  );
};

export default React.memo(GeneralInformation);
